import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, Injector, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { FuseSplashScreenService } from '@fuse/services/splash-screen';
import { filter, map, mergeMap } from 'rxjs';
import { AnalyticsPageViewService } from './core/services/analytics-pageview.service';
import { PerformanceService } from './core/services/performance.service';
import { SEOService } from './core/services/seo.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private splashScreenService: FuseSplashScreenService,
        private injector: Injector,
        private _seoService: SEOService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private performanceService: PerformanceService,
        private swUpdate: SwUpdate
    ) {}

    ngOnInit(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map((route) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }

                    return route;
                }),
                filter((route) => route.outlet === 'primary'),
                mergeMap((route) => route.data)
            )
            .subscribe((data) => {
                this._seoService.updateTitle(data['title']);
                this._seoService.updateDescription(data['description']);
                this._seoService.updateKeywords(data['keywords']);
                this._seoService.updateOgTitle(data['ogTitle']);
                this._seoService.updateOgType(data['ogType']);
                this._seoService.updateOgDescription(data['ogDescription']);
                this._seoService.updateOgImage(data['ogImage']);
                this._seoService.updateOgUrl(data['ogUrl']);
                this._seoService.updateTwitterCard(data['twCard']);
                this._seoService.updateTwitterTitle(data['twTitle']);
                this._seoService.updateTwitterDescription(data['twDescription']);
                this._seoService.updateTwitterImage(data['twImage']);
                this._seoService.updateCanonical(data['canonical']);
            });

        if (isPlatformBrowser(this.platformId)) {
            // SSR content is prerendered — body starts with fuse-splash-screen-hidden
            // so content is visible immediately. Just ensure it stays hidden.
            this.splashScreenService.hide();
            this.performanceService.measurePageLoad();

            this.injector.get(AnalyticsPageViewService).init();
            this.registerSwUpdates();
        }
    }

    private registerSwUpdates(): void {
        if (!this.swUpdate.isEnabled) return;

        this.swUpdate.versionUpdates
            .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
            .subscribe(() => {
                this.swUpdate.activateUpdate().then(() => document.location.reload());
            });

        this.swUpdate.checkForUpdate().catch(() => void 0);
    }
}
